import {useEffect} from 'react';

import {zodResolver} from '@hookform/resolvers/zod';
import {Alert, Box, DialogActions, DialogContent, MenuItem, Select} from '@mui/material';
import Grid from '@mui/material/Grid';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {Controller, FormProvider, useForm} from 'react-hook-form';

import {WINDOWS_TIMEZONES} from '../../../../../../const';
import useReportParameters from '../../../../../../hooks/providers/useReportParameters';
import {ianaToWindowsMap} from '../../../../../../utils';
import Switch from '../../../../../emmai-forms/forms-utils/field-components/Switch/Switch';
import Button from '../../../../../form/buttons/Button/Button';
import ExecutionTimesSelector from '../ExecutionTimesSelector/ExecutionTimesSelector';
import FieldLabel from '../FieldLabel/FieldLabel';
import {deserializePlannedUpdate, formatSelectedDays, plannedUpdateSchema, WEEK_DAYS} from '../utils/utils';
import WeekdaySelector from '../WeekdaySelector/WeekdaySelector';

const PlannedUpdateForm = ({onClose}) => {
  const {currentReport, scheduleReportUpdate} = useReportParameters();
  const userTz = dayjs.tz.guess();
  const windowsUserTz = ianaToWindowsMap[userTz];

  const methods = useForm({
    resolver: zodResolver(plannedUpdateSchema),
    mode: 'all',
    defaultValues: {
      weekdays: [],
      enabled: true,
      executionTimes: [],
      localTimeZoneId: windowsUserTz
    }
  });
  const {reset, formState, handleSubmit, control, watch} = methods;

  const {isValid, isDirty} = formState;
  const enabled = watch('enabled');
  const weekdays = watch('weekdays');

  const onSubmit = values => {
    scheduleReportUpdate(values);
    onClose();
  };

  useEffect(() => {
    if (currentReport?.refreshSchedule) {
      reset(deserializePlannedUpdate(currentReport.refreshSchedule));
    }
  }, [currentReport?.refreshSchedule]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Alert severity="info" sx={{mb: 1, mt: -0.5}}>
          Les actualisations seront exécutées aux heures spécifiées, {formatSelectedDays(weekdays)}
        </Alert>
        <DialogContent sx={{py: 2.5}}>
          <Grid container spacing={4}>
            <WeekdaySelector enabled={enabled} />

            <Grid item xs={12} md={7}>
              <Grid container alignItems="center" justifyContent="space-between">
                <FieldLabel>{enabled ? 'Activé' : 'Désactivé'}</FieldLabel>
                <Controller name="enabled" control={control} render={({field}) => <Switch checked={!!field.value} onChange={e => field.onChange(e.target.checked)} />} />
              </Grid>

              <Box mt={2}>
                <FieldLabel>Fuseau horaire</FieldLabel>
                <Controller
                  disabled={!enabled}
                  name="localTimeZoneId"
                  control={control}
                  render={({field}) => (
                    <Select {...field} size="small" fullWidth>
                      {WINDOWS_TIMEZONES.map(tz => (
                        <MenuItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Box>

              <ExecutionTimesSelector enabled={enabled} />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{justifyContent: 'center', mb: 2}}>
          <Button variant="outlined" color="error" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" variant="contained" color="secondary" disabled={!isValid || !isDirty}>
            Enregistrer la planification
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

PlannedUpdateForm.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default PlannedUpdateForm;
